import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

const fin = {
  primary: "#009db9",
  primaryDark: "#007a8a",
  primarySoft: "#e6f6f9",
  text: "#1f2933",
  muted: "#6b7280",
  border: "#e5e7eb",
  surface: "#f7f9fb",
  radius: "16px",
  shadow: "0 4px 20px rgba(16, 24, 40, 0.06)",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#fff",
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: fin.primary },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: fin.primary,
    },
    "&.Mui-disabled": { backgroundColor: "#f3f5f7" },
  },
  "& label.Mui-focused": { color: fin.primary },
};

const primaryButtonSx = {
  backgroundColor: fin.primary,
  color: "#fff",
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 700,
  px: 3,
  py: 1.25,
  boxShadow: "none",
  "&:hover": { backgroundColor: fin.primaryDark, boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#d5dbe0", color: "#8a949e" },
};

const selectMenuProps = { PaperProps: { sx: { maxHeight: 320 } } };

const MoneyField = ({ label, value, onChange, disabled = false, helperText }) => (
  <TextField
    label={label}
    type="number"
    value={value}
    onChange={onChange}
    disabled={disabled}
    helperText={helperText}
    variant="outlined"
    fullWidth
    sx={fieldSx}
    InputProps={{
      startAdornment: <InputAdornment position="start">KSh</InputAdornment>,
    }}
  />
);

const SectionCard = ({ title, action, children }) => (
  <Card
    sx={{
      mb: { xs: 2.5, md: 4 },
      borderRadius: fin.radius,
      border: `1px solid ${fin.border}`,
      backgroundColor: "#fff",
      boxShadow: fin.shadow,
      overflow: "visible",
    }}
  >
    <CardContent
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        "&:last-child": { pb: { xs: 2, sm: 3, md: 4 } },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
          mb: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: fin.text,
            pl: 1.5,
            borderLeft: `4px solid ${fin.primary}`,
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
        {action}
      </Box>
      {children}
    </CardContent>
  </Card>
);

const IOSSwitch = styled(Switch)(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  flexShrink: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#65C466",
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": { opacity: 0.5 },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#33cf4d",
      border: "6px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": { color: theme.palette.grey[100] },
    "&.Mui-disabled + .MuiSwitch-track": { opacity: 0.7 },
  },
  "& .MuiSwitch-thumb": { boxSizing: "border-box", width: 22, height: 22 },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: "#1976d2",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
  },
}));

const AddEarningForm = ({
  selectedUserType,
  setSelectedUserType,
  selectedUser,
  setSelectedUser,
  employeeId,
  setEmployeeId,
  email,
  setEmail,
  selectedBankAccount,
  setSelectedBankAccount,
  bankAccounts,
  bankLoading,
  bankHelperText,
  users,
  grossPay,
  setGrossPay,
  bonus,
  setBonus,
  tax,
  setTax,
  withholdingTax,
  setWithholdingTax,
  housingLevy,
  setHousingLevy,
  sha,
  setSha,
  nssf,
  setNssf,
  paymentDate,
  setPaymentDate,
  paymentStatus,
  setPaymentStatus,
  isBonusPayment,
  handlePaymentTypeToggle,
  handleSubmit,
  submitting,
  formatBankAccountDisplay,
}) => {
  return (
    <SectionCard title="Add earning">
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            sm: "repeat(2, minmax(0, 1fr))",
          },
          gap: 2.5,
        }}
      >
        <TextField
          select
          label="User type"
          value={selectedUserType}
          onChange={(e) => {
            setSelectedUserType(e.target.value);
            setSelectedUser("");
            setTax("");
            setWithholdingTax("");
            setHousingLevy("");
            setNssf("");
            setSha("");
          }}
          fullWidth
          sx={fieldSx}
          SelectProps={{ MenuProps: selectMenuProps }}
        >
          <MenuItem value="Employee">Employee</MenuItem>
          <MenuItem value="Intern">Intern</MenuItem>
          <MenuItem value="Consultant">Consultant</MenuItem>
        </TextField>
        <TextField
          select
          label="User"
          value={selectedUser}
          onChange={(e) => setSelectedUser(e.target.value)}
          fullWidth
          sx={fieldSx}
          SelectProps={{ MenuProps: selectMenuProps }}
        >
          {users.map((u) => (
            <MenuItem key={u.id} value={u.name}>
              {u.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Employee ID"
          value={employeeId}
          onChange={(e) => setEmployeeId(e.target.value)}
          variant="outlined"
          fullWidth
          sx={fieldSx}
          disabled={!!selectedUser}
        />
        <TextField
          label="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          variant="outlined"
          fullWidth
          sx={fieldSx}
          disabled={!!selectedUser}
        />
        <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
          <TextField
            select
            label="Payment method"
            value={selectedBankAccount}
            onChange={(e) => setSelectedBankAccount(e.target.value)}
            disabled={!selectedUser || bankAccounts.length === 0}
            error={!!selectedUser && !bankLoading && bankAccounts.length === 0}
            helperText={bankHelperText}
            fullWidth
            sx={fieldSx}
            SelectProps={{ MenuProps: selectMenuProps }}
            InputProps={{
              endAdornment: bankLoading ? (
                <InputAdornment position="end" sx={{ mr: 3 }}>
                  <CircularProgress size={18} sx={{ color: fin.primary }} />
                </InputAdornment>
              ) : undefined,
            }}
          >
            {bankAccounts.map((a) => (
              <MenuItem key={a.id} value={a.id}>
                {formatBankAccountDisplay(a)}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Divider sx={{ gridColumn: "1 / -1" }} />
        <Box
          sx={{
            gridColumn: "1 / -1",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            p: 2,
            borderRadius: "12px",
            border: `1px solid ${fin.border}`,
            backgroundColor: isBonusPayment ? fin.primarySoft : fin.surface,
            transition: "background-color 0.2s ease",
          }}
        >
          <Typography sx={{ fontWeight: 600, color: fin.text }}>
            Bonus Payment
          </Typography>
          <IOSSwitch
            checked={isBonusPayment}
            onChange={handlePaymentTypeToggle}
            inputProps={{ "aria-label": "Bonus payment" }}
          />
        </Box>

        {!isBonusPayment && (
          <MoneyField
            label="Gross pay"
            value={grossPay}
            onChange={(e) => setGrossPay(e.target.value)}
          />
        )}
        <MoneyField
          label="Bonus"
          value={bonus}
          onChange={(e) => setBonus(e.target.value)}
        />
        {!isBonusPayment && (
          <MoneyField
            label="Tax"
            value={selectedUserType === "Employee" ? tax : ""}
            onChange={(e) => {
              if (selectedUserType === "Employee") setTax(e.target.value);
            }}
            disabled={selectedUserType !== "Employee"}
          />
        )}
        {!isBonusPayment && (
          <MoneyField
            label="Withholding tax"
            value={selectedUserType === "Consultant" ? withholdingTax : ""}
            onChange={(e) => {
              if (selectedUserType === "Consultant")
                setWithholdingTax(e.target.value);
            }}
            disabled={selectedUserType !== "Consultant"}
          />
        )}
        {!isBonusPayment && (
          <MoneyField
            label="Housing levy"
            value={selectedUserType === "Employee" ? housingLevy : ""}
            onChange={(e) => {
              if (selectedUserType === "Employee")
                setHousingLevy(e.target.value);
            }}
            disabled={selectedUserType !== "Employee"}
          />
        )}
        {!isBonusPayment && (
          <MoneyField
            label="SHA"
            value={selectedUserType === "Employee" ? sha : ""}
            onChange={(e) => {
              if (selectedUserType === "Employee") setSha(e.target.value);
            }}
            disabled={selectedUserType !== "Employee"}
          />
        )}
        {!isBonusPayment && (
          <MoneyField
            label="NSSF"
            value={selectedUserType === "Employee" ? nssf : ""}
            onChange={(e) => {
              if (selectedUserType === "Employee") setNssf(e.target.value);
            }}
            disabled={selectedUserType !== "Employee"}
          />
        )}

        <Divider sx={{ gridColumn: "1 / -1" }} />
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Payment Date*"
            value={paymentDate}
            onChange={setPaymentDate}
            slotProps={{
              textField: {
                fullWidth: true,
                sx: fieldSx,
                helperText: "Any date can be selected",
              },
            }}
          />
        </LocalizationProvider>
        <TextField
          select
          label="Payment Status*"
          value={paymentStatus}
          onChange={(e) => setPaymentStatus(e.target.value)}
          fullWidth
          sx={fieldSx}
        >
          <MenuItem value="Yes">Paid</MenuItem>
          <MenuItem value="No">Not paid</MenuItem>
        </TextField>

        <Box
          sx={{
            gridColumn: "1 / -1",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "flex-end",
            gap: 2,
            pt: 2.5,
            borderTop: `1px solid ${fin.border}`,
          }}
        >
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!paymentDate || !paymentStatus || submitting}
            startIcon={
              submitting ? (
                <CircularProgress size={18} sx={{ color: "inherit" }} />
              ) : null
            }
            sx={primaryButtonSx}
          >
            {submitting ? "Saving..." : "Save earning"}
          </Button>
        </Box>
      </Box>
    </SectionCard>
  );
};

export default AddEarningForm;